import {
  Brand as BrandModel,
  Comment,
  Country as CountryModel,
  Follow,
  Port as PortModel,
  Product,
  ProductCountry,
  Provider,
  Season,
  User
} from '@app/models';
import { BusinessTypes } from '@app/models/business-unit';
import { Department, getAncestorsIndex } from '@app/models/department';
import { FollowTypes, getFollowDepartmentName } from '@app/models/follow';
import { getCbm, getComposedDescriptionColor } from '@app/product.utils';
import * as rolesUtils from '@app/role.utils';
import { groupBy } from '@app/utils';
import * as fromAuth from '@core/reducers/auth.reducer';
import * as fromAutocomplete from '@core/reducers/autocomplete.reducer';
import * as fromBrands from '@core/reducers/brand.reducer';
import * as fromBusinessUnits from '@core/reducers/business-unit.reducer';
import * as fromColors from '@core/reducers/color.reducer';
import * as fromComments from '@core/reducers/comment.reducer';
import * as fromCountryDepartments from '@core/reducers/country-department.reducer';
import * as fromCountries from '@core/reducers/country.reducer';
import * as fromDepartmentsFood from '@core/reducers/department-food.reducer';
import * as fromDepartments from '@core/reducers/department.reducer';
import * as fromDestination from '@core/reducers/destination.reducer';
import * as fromFiles from '@core/reducers/file.reducer';
import * as fromFollowGroups from '@core/reducers/follow-group.reducer';
import * as fromFollowShipments from '@core/reducers/follow-shipment.reducer';
import * as fromFollows from '@core/reducers/follow.reducer';
import * as fromHangers from '@core/reducers/hanger.reducer';
import * as fromMe from '@core/reducers/me.reducer';
import * as fromNotifications from '@core/reducers/notification.reducer';
// import * as fromPackagingManagers from '@core/reducers/packaging-manager.reducer';
import * as fromPlannerIds from '@core/reducers/planner-id.reducer';
import * as fromPorts from '@core/reducers/port.reducer';
import * as fromProductCountries from '@core/reducers/product-country.reducer';
import * as fromProductShipments from '@core/reducers/product-shipment.reducer';
import * as fromProducts from '@core/reducers/product.reducer';
import * as fromProviders from '@core/reducers/provider.reducer';
import * as fromSeasons from '@core/reducers/season.reducer';
import * as fromShippingTimes from '@core/reducers/shipping-time.reducer';
import * as fromUsers from '@core/reducers/user.reducer';
import { createFeatureSelector, createSelector } from '@ngrx/store';

// feature selectors
export const getDepartmentsState = createFeatureSelector<fromDepartments.State>(
  'departments'
);

export const getDepartmentsFoodState = createFeatureSelector<fromDepartmentsFood.State>(
  'departmentsFood'
);

export const getNotificationsState = createFeatureSelector<
  fromNotifications.State
>('notifications');

export const getColorsState = createFeatureSelector<fromColors.State>('colors');
export const getPlannerIdsState = createFeatureSelector<fromPlannerIds.State>(
  'plannerIds'
);

export const getAuthState = createFeatureSelector<fromAuth.State>('auth');

export const getProvidersState = createFeatureSelector<fromProviders.State>(
  'providers'
);

export const getShippingTimesState = createFeatureSelector<
  fromShippingTimes.State
>('shippingTimes');

export const getSeasonsState = createFeatureSelector<fromSeasons.State>(
  'seasons'
);

export const getUsersState = createFeatureSelector<fromUsers.State>('users');

export const getCountriesState = createFeatureSelector<fromCountries.State>(
  'countries'
);

export const getPortsState = createFeatureSelector<fromPorts.State>('ports');

export const getFilesState = createFeatureSelector<fromFiles.State>('files');

// export const getPackagingManagersState = createFeatureSelector<
//   fromPackagingManagers.State
// >('packagingManagers');

export const getCommentsState = createFeatureSelector<fromComments.State>(
  'comments'
);

export const getBusinessUnitsState = createFeatureSelector<
  fromBusinessUnits.State
>('businessUnits');

export const getFollowGroupsState = createFeatureSelector<
  fromFollowGroups.State
>('followGroups');

export const getProductsState = createFeatureSelector<fromProducts.State>(
  'products'
);

export const getProductCountriesState = createFeatureSelector<
  fromProductCountries.State
>('productCountries');

export const getAutocompleteState = createFeatureSelector<
  fromAutocomplete.State
>('autocomplete');

export const getBrandsState = createFeatureSelector<fromBrands.State>('brands');

export const getHangersState = createFeatureSelector<fromHangers.State>(
  'hangers'
);

export const getMeState = createFeatureSelector<fromMe.State>('me');

// getters
export const getDepartments = createSelector(
  getDepartmentsState,
  fromDepartments.selectAll
);

export const getDepartmentsFood = createSelector(
  getDepartmentsFoodState,
  fromDepartmentsFood.selectAll
);

export const getTopDepartments = createSelector(
  getDepartments,
  (departments: Department[]) =>
    departments.filter(department => department.level === 1)
);

export const getColors = createSelector(
  getColorsState,
  fromColors.selectAll
);

export const getDepartmentEntities = createSelector(
  getDepartmentsState,
  fromDepartments.selectEntities
);

export const getDepartmentFoodEntities = createSelector(
  getDepartmentsFoodState,
  fromDepartmentsFood.selectEntities
);

export const getSelectedDepartmentId = createSelector(
  getDepartmentsState,
  fromDepartments.selectSelectedId
);

export const getSelectedDepartment = createSelector(
  getDepartmentEntities,
  getSelectedDepartmentId,
  (entities, id) => {
    if (id && entities) {
      return entities[id];
    }
  }
);

export const getProviders = createSelector(
  getProvidersState,
  fromProviders.selectAll
);

export const getPlannerIds = createSelector(
  getPlannerIdsState,
  fromPlannerIds.selectAll
);

export const getShippingTimes = createSelector(
  getShippingTimesState,
  fromShippingTimes.selectAll
);

export const getProviderEntities = createSelector(
  getProvidersState,
  fromProviders.selectEntities
);

export const getFiles = createSelector(
  getFilesState,
  fromFiles.selectAll
);
export const getExportingFile = createSelector(
  getFilesState,
  fromFiles.selectExporting
);

// export const getPackagingUsers = createSelector(
//   getPackagingManagersState,
//   fromPackagingManagers.getPackagingUsers
// );

// export const getIsPackagingManagerLoading = createSelector(
//   getPackagingManagersState,
//   fromPackagingManagers.getIsLoading
// );

export const getSeasons = createSelector(
  getSeasonsState,
  fromSeasons.selectAll
);

export const getSeasonEntities = createSelector(
  getSeasonsState,
  fromSeasons.selectEntities
);

export const getSelectedSeasonId = createSelector(
  getSeasonsState,
  fromSeasons.selectSelectedId
);

export const getSelectedSeason = createSelector(
  getSeasonEntities,
  getSelectedSeasonId,
  (entities, id) => {
    if (id && entities) {
      return entities[id];
    }
  }
);

export const getNotifications = createSelector(
  getNotificationsState,
  fromNotifications.selectAll
);

export const getNotificationsCounter = createSelector(
  getNotificationsState,
  fromNotifications.selectCounter
);

export const getProductSeasonsAC = createSelector(
  getAutocompleteState,
  fromAutocomplete.selectProductSeasons
);
export const getProductQualitiesAC = createSelector(
  getAutocompleteState,
  fromAutocomplete.selectProductQualities
);
export const getProductTripDatesAC = createSelector(
  getAutocompleteState,
  fromAutocomplete.selectProductTripDates
);

export const getProductCollectionsAC = createSelector(
  getAutocompleteState,
  fromAutocomplete.selectProductCollections
);

export const getProductTagsAC = createSelector(
  getAutocompleteState,
  fromAutocomplete.selectProductTags
);

// export const getProductCountryTypesAC = createSelector(
//   getAutocompleteState,
//   fromAutocomplete.selectProductCountryTypes
// );
// export const getProductBusinessUnitsAC = createSelector(
//   getAutocompleteState,
//   fromAutocomplete.selectProductBusinessUnits
// );

export const getCountries = createSelector(
  getCountriesState,
  fromCountries.selectAll
);

export const getFeaturedCountries = createSelector(
  getCountries,
  (countries: CountryModel[]) => countries.filter(country => country.featured)
);

export const getCountryEntities = createSelector(
  getCountriesState,
  fromCountries.selectEntities
);

export const getSelectedCountryId = createSelector(
  getCountriesState,
  fromCountries.selectSelectedId
);

export const getSelectedCountry = createSelector(
  getSelectedCountryId,
  getCountryEntities,
  (id, entities) => {
    return id ? entities[id] : null;
  }
);

export const getBusinessUnits = createSelector(
  getBusinessUnitsState,
  fromBusinessUnits.selectAll
);

export const getFollowGroups = createSelector(
  getFollowGroupsState,
  fromFollowGroups.selectAll
);

export const getFollowGroupEntities = createSelector(
  getFollowGroupsState,
  fromFollowGroups.selectEntities
);

export const getBusinessUnitEntities = createSelector(
  getBusinessUnitsState,
  fromBusinessUnits.selectEntities
);

export const getUsers = createSelector(
  getUsersState,
  fromUsers.selectAll
);
export const getUsersWithFullName = createSelector(
  getUsers,
  users =>
    users.map(user => ({
      ...user,
      fullName: `${user.firstName} ${user.lastName}`
    }))
);

export const getUserEntities = createSelector(
  getUsersState,
  fromUsers.selectEntities
);

export const getCountryDepartmentEntities = createSelector(
  fromCountryDepartments.getState,
  fromCountryDepartments.selectEntities
);

export const getProducts = createSelector(
  getProductsState,
  fromProducts.selectAll
);

// export const getProducts = createSelector(getProductItems, items =>
//   items.filter(
//     item =>
//       !item.metadata.productType ||
//       (item.metadata.productType &&
//         item.metadata.productType.toUpperCase().includes('INDIVIDUAL'))
//   )
// );

// export const getSets = createSelector(getProductItems, items =>
//   items.filter(
//     item =>
//       item.metadata.productType &&
//       item.metadata.productType.toUpperCase().includes('SET')
//   )
// );

// export const getComponents = createSelector(getProductItems, items =>
//   items.filter(
//     item =>
//       item.metadata.productType &&
//       item.metadata.productType.toUpperCase().includes('COMPONENTE')
//   )
// );

export const getFollowProductsLoaded = createSelector(
  getProductsState,
  fromProducts.selectFollowsLoaded
);

export const getProductEntities = createSelector(
  getProductsState,
  fromProducts.selectEntities
);

export const getBrands = createSelector(
  getBrandsState,
  fromBrands.selectAll
);

export const getHangers = createSelector(
  getHangersState,
  fromHangers.selectAll
);

export const getBrandEntities = createSelector(
  getBrandsState,
  fromBrands.selectEntities
);

export const getComments = createSelector(
  getCommentsState,
  fromComments.selectAll
);

export const getCommentEntities = createSelector(
  getCommentsState,
  fromComments.selectEntities
);

export const getProductCountries = createSelector(
  getProductCountriesState,
  fromProductCountries.selectAll
);

export const getProductCountriesEntities = createSelector(
  getProductCountriesState,
  fromProductCountries.selectEntities
);

export const getPorts = createSelector(
  getPortsState,
  fromPorts.selectAll
);
export const getPortEntities = createSelector(
  getPortsState,
  fromPorts.selectEntities
);

export const getMe = createSelector(
  getMeState,
  fromMe.selectUser
);

// populated
export const getCommentsPopulated = createSelector(
  getComments,
  getUserEntities,
  getProductEntities,
  (
    comments: Comment[],
    userEntities: { [id: number]: User },
    productEntities: { [id: number]: Product }
  ) =>
    comments.map(comment => {
      const Author = userEntities[comment.AuthorId];
      const product = comment.CommentableId
        ? productEntities[comment.CommentableId]
        : null;
      return { ...comment, Author, product };
    })
);

export const getIndexedDepartments = createSelector(
  getDepartments,
  getDepartmentEntities,
  (departments: Department[], entities) => {
    return departments
      .sort((a, b) => {
        return a.level - b.level;
      })
      .map(department => {
        const res = {
          ...department,
          indexed: getAncestorsIndex(department, entities, '')
        };
        return res;
      });
  }
);

// tree leaves
// export const getN2Departments = createSelector(getDepartments, departments =>
//   departments.filter(
//     department => !departments.map(d => d.ParentId).includes(department.id)
//   )
// );

export const getFollowsPopulated = createSelector(
  fromFollows.getFollows,
  getSeasonEntities,
  getDepartmentEntities,
  getFollowGroupEntities,
  (
    follows: Follow[],
    seasonEntities: { [id: number]: Season },
    departmentEntities: { [id: number]: Department },
    followGroupEntities
  ) =>
    follows.map(follow => {
      const season = seasonEntities[follow.SeasonId];
      const followGroup = follow.FollowGroupId
        ? followGroupEntities[follow.FollowGroupId]
        : {};
      const department = departmentEntities[follow.DepartmentId]
        ? {
          ...departmentEntities[follow.DepartmentId],
          indexed: getFollowDepartmentName(follow, departmentEntities)
        }
        : {};

      return { ...follow, season, department, followGroup };
    })
);

export const getProductCountriesByProductId = createSelector(
  getProductCountries,
  getMe,
  (countries, me) =>
    countries
      .filter(country => {
        if (rolesUtils.isCountryUser(me.role)) {
          return (
            country.CountryId === me.CountryId &&
            (!country.businessUnit || country.businessUnit === me.businessUnit)
          );
        } else {
          return true;
        }
      })
      .reduce((obj, country) => {
        const ProductId = country.ProductId;
        if (obj[ProductId]) {
          obj[ProductId].push(country);
        } else {
          obj[ProductId] = [country];
        }
        return obj;
      }, {})
);

export const getProductsPrePopulated = createSelector(
  getProducts,
  getDepartmentEntities,
  getBrandEntities,
  getPortEntities,
  getProviderEntities,
  getCountryEntities,
  getProductCountriesByProductId,
  fromProductShipments.getGroupedByProductCountryId,
  (
    products: Product[],
    departmentEntities: { [id: number]: Department },
    brandEntities: { [id: number]: BrandModel },
    portEntities: { [id: number]: PortModel },
    providerEntities: { [id: number]: Provider },
    countryEntities: { [id: number]: CountryModel },
    groupedProductCountries: { [ProductId: number]: ProductCountry[] },
    groupedProductShipments
  ) =>
    products.map(product => {
      const productProvider = providerEntities[product.ProviderId];
      const cbmPerCarton = getCbm(product);
      const countryOfOrigin =
        product &&
          product.CountryId &&
          countryEntities[product.CountryId]
          ? countryEntities[product.CountryId].name || 'No definido'
          : 'No definido';
      const populatedProvider = {
        ...productProvider,
        Country:
          productProvider &&
            productProvider.CountryId &&
            countryEntities[productProvider.CountryId]
            ? countryEntities[productProvider.CountryId].name
            : null
      };

      const Brand = brandEntities[product.BrandId];
      let Port = portEntities[product.PortId];
      let ProductCountries = groupedProductCountries[product.id] || [];

      Port = Port
        ? { ...Port, Country: countryEntities[Port.CountryId] }
        : Port;

      ProductCountries = ProductCountries.map(pc => {
        const Shipments = groupedProductShipments
          ? groupedProductShipments[pc.id]
          : [];

        return { ...pc, Country: countryEntities[pc.CountryId], Shipments };
      });

      ProductCountries = ProductCountries.sort(
        (a, b) =>
          (a.Country &&
            b.Country &&
            a.Country.name.localeCompare(b.Country.name)) ||
          1
      );
      const department = departmentEntities[product.DepartmentId]
        ? {
          ...departmentEntities[product.DepartmentId],
          indexed: getAncestorsIndex(
            departmentEntities[product.DepartmentId],
            departmentEntities,
            ''
          )
        }
        : {};
      return {
        ...product,
        department,
        Provider: populatedProvider,
        Port,
        Brand,
        ProductCountries,
        countryOfOrigin,
        cbmPerCarton
      };
    })
);

export const getProductsPopulated = createSelector(
  getProductsPrePopulated,
  fromFollows.getFollowEntities,
  (products, followEntities, shipments) => {
    return products.map(product => {
      const follow = followEntities[product.FollowId];
      const followType = follow ? follow.type : 'Unknown';

      const businessUnits = product.ProductCountries.map(
        pc => pc.businessUnit
      ).reduce((arr, item) => {
        const found = arr.find(x => x === item);
        if (!found && item !== null) {
          arr = [...arr, item];
        }
        return arr;
      }, []);

      const dynamicSkus = product
        ? product.ProductCountries.filter(
          pc =>
            pc.businessUnit !== null &&
            pc.businessUnit !== BusinessTypes.HomeImprovement
        ).reduce((obj, pc) => {
          obj[`sku-${pc.businessUnit}-${pc.Country && pc.Country.name}`] =
            pc && pc.metadata && pc.metadata.sku;
          return obj;
        }, {})
        : {};

      const countryBusinessUnits = product.ProductCountries.map(pc => {
        if (pc.CountryId && pc.Country && pc.businessUnit) {
          return `${pc.businessUnit} - ${pc.Country.name}`;
        } else {
          return null;
        }
      }).reduce((arr, item) => {
        const found = arr.find(x => x === item);
        if (!found && item !== null) {
          arr = [...arr, item];
        }
        return arr;
      }, []);

      return {
        ...product,
        ...dynamicSkus,
        businessUnits,
        countryBusinessUnits,
        followType,
        descriptionColor:
          followType === FollowTypes.Clothing
            ? getComposedDescriptionColor(product)
            : product.descriptionColor
      };
    });
  }
);

export const getMePopulated = createSelector(
  getMe,
  getCountryEntities,
  getFollowGroupEntities,
  (
    me: any,
    countryEntities: { [id: number]: CountryModel },
    followGroupEntities
  ) => {
    const Country = me && countryEntities ? countryEntities[me.CountryId] : {};
    const FollowGroup =
      me && followGroupEntities ? followGroupEntities[me.FollowGroupId] : {};
    return { ...me, Country, FollowGroup };
  }
);

export const getProvidersPopulated = createSelector(
  getProviders,
  getCountryEntities,
  // getPortEntities,
  (
    providers: Provider[],
    countryEntities: { [id: number]: CountryModel },
    portEntities: { [id: number]: PortModel }
  ) => {
    return providers.map(provider => {
      const country = countryEntities[provider.CountryId];
      const Ruts = Object.keys(provider.ruts).map(key => {
        const rutCountry = countryEntities[key];
        return { country: rutCountry, value: provider.ruts[key] };
      });
      // const ProviderPorts = provider.ProviderPorts.map(pp => {
      //   const port = portEntities[pp.PortId];
      //   return { ...pp, port };
      // });

      return { ...provider, country, Ruts };
    });
  }
);

export const getFollowShipmentsPopulated = createSelector(
  fromFollowShipments.getAll,
  getCountryEntities,
  (shipments, entities) =>
    shipments.map(shipment => {
      return { ...shipment, Origin: entities[shipment.CountryId] };
    })
);

export const getPortsPopulated = createSelector(
  getPorts,
  getCountryEntities,
  (ports, countryEntities) => {
    return ports.map(port => {
      const Country = countryEntities[port.CountryId];
      return { ...port, Country };
    });
  }
);

export const getActivity = createSelector(
  fromFollows.getFollowsState,
  getProductsState,
  (followsState, productsState) => {
    const state = !!(
      followsState.loading ||
      followsState.creatingSetting ||
      followsState.loadingCountries ||
      productsState.loading ||
      productsState.updating
    );
    return state;
  }
);

export const getShippingTimesGSP = createSelector(
  getShippingTimes,
  getCountryEntities,
  (times, countryEntities) => {
    if (times && countryEntities) {
      const populated = times.map(time => {
        return {
          ...time,
          Origin: countryEntities[time.OriginId] || {
            name: 'Sin nombre origen'
          },
          Destination: countryEntities[time.DestinationId] || {
            name: 'Sin nombre destino'
          }
        };
      });
      return populated.reduce((obj, shippingTime) => {
        const originName =
          shippingTime && shippingTime.Origin && shippingTime.Origin.name;
        if (originName && obj[originName]) {
          obj[originName] = [...obj[originName], shippingTime];
        } else if (originName && !obj[originName]) {
          obj[originName] = [shippingTime];
        }
        return obj;
      }, {});
    }
  }
);

export const getProductsByFollow = createSelector(
  getProductsPopulated,
  (products: Product[]) => {
    return products ? groupBy(products, product => product.FollowId) : {};
  }
);

export const getDestinationState = createFeatureSelector<fromDestination.State>(
  'destinations'
);

export const getDestination = createSelector(
  getDestinationState,
  fromDestination.selectAll
);

// export const getProformaPopulated = createSelector(
//   getProviderEntities,
//   providers => {}
// );
// export const getSelectedProforma = createSelector()
